// pkg191 — admin session plumbing (D-S3/D-S4): ONE place that owns the admin token storage keys, a single-flight refreshAccessToken() so twenty parallel 403s cause one rotation (the backend revokes the used refresh token — a second concurrent call would 401 and log the user out), tokenSecondsLeft() to decode the JWT exp, and ensureFreshToken() for refresh-on-wake; a failed refresh clears storage and broadcasts lm:session-expired so AdminAuthContext signs out and the login page explains why — delivered 2026-10-31
//
// Consumers: contexts/AdminAuthContext.js (login/startup/wake), utils/adminApi.js
// (403 AUTH_REQUIRED → refresh → retry). The storage keys are the pre-pkg191
// values byte-for-byte so an existing browser keeps its session across deploy.

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const SS_TOKEN   = 'ryanlaw_admin_token';   // sessionStorage — non-persistent
export const LS_TOKEN   = 'ryanlaw_admin_token_p'; // localStorage   — persistent
export const LS_REFRESH = 'ryanlaw_admin_refresh'; // localStorage   — refresh token
export const LS_REMEMBER_PREF = 'ryanlaw_admin_remember_me'; // '1' | '0' — the checkbox choice (D-S5)
export const SS_EXPIRED_FLAG  = 'ryanlaw_admin_session_expired'; // set when a refresh fails; login page reads + clears it

export const SESSION_EXPIRED_EVENT = 'lm:session-expired';

export function getStoredToken() {
  return sessionStorage.getItem(SS_TOKEN) || localStorage.getItem(LS_TOKEN);
}

export function setStoredToken(token, persistent) {
  if (persistent) {
    localStorage.setItem(LS_TOKEN, token);
    sessionStorage.removeItem(SS_TOKEN);
  } else {
    sessionStorage.setItem(SS_TOKEN, token);
    localStorage.removeItem(LS_TOKEN);
  }
}

export function clearStoredToken() {
  sessionStorage.removeItem(SS_TOKEN);
  localStorage.removeItem(LS_TOKEN);
}

export function getRefreshToken() {
  return localStorage.getItem(LS_REFRESH);
}

export function clearSession() {
  clearStoredToken();
  localStorage.removeItem(LS_REFRESH);
}

// Seconds until the access JWT expires; null when there is no token or it
// can't be decoded (treated as "refresh now" by callers). Decode only — the
// signature is the server's business.
export function tokenSecondsLeft(token = getStoredToken()) {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (!payload.exp) return null;
    return Math.floor(payload.exp - Date.now() / 1000);
  } catch {
    return null;
  }
}

// Single-flight. While a rotation is in progress every caller awaits the same
// promise and receives the same new access token (or null).
let inFlight = null;

export function refreshAccessToken() {
  if (inFlight) return inFlight;
  const raw = getRefreshToken();
  if (!raw) return Promise.resolve(null);

  inFlight = (async () => {
    try {
      const res = await fetch(`${BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: raw }),
      });
      if (!res.ok) {
        // 401 REFRESH_INVALID (expired/revoked) is terminal. A 5xx or a
        // network failure is NOT — keep the stored tokens so the next
        // attempt can still succeed; the caller just doesn't get a token now.
        if (res.status === 401 || res.status === 400) {
          expireSession();
        }
        return null;
      }
      const data = await res.json();
      setStoredToken(data.token, true);
      localStorage.setItem(LS_REFRESH, data.refreshToken);
      return data.token;
    } catch {
      return null; // network blip — keep tokens, try again later
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

// Refresh if the access token is missing or has less than `thresholdSec`
// left and a refresh token exists. Returns the current-or-new token.
export async function ensureFreshToken(thresholdSec = 60 * 60) {
  if (!getRefreshToken()) return getStoredToken();
  const left = tokenSecondsLeft();
  if (left !== null && left > thresholdSec) return getStoredToken();
  const fresh = await refreshAccessToken();
  return fresh || getStoredToken();
}

// Terminal: the refresh token is gone/invalid. Clear everything, leave a flag
// for the login page, and tell the auth context to drop the user.
export function expireSession() {
  clearSession();
  try { sessionStorage.setItem(SS_EXPIRED_FLAG, '1'); } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}
